import React, { useState, useEffect, useRef } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import CandidateKeyDates from './CandidateKeyDates';
import CopyLinkButton from './CopyLinkButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Phone, Building2, Clock, Calendar, Briefcase,
  Edit, Trash2, Plus, Star, MessageSquare, History, CheckCircle, XCircle,
  AlertCircle, Loader2, FileText, Tag, IndianRupee, Download, Upload, Eye,
  File, RefreshCw, MapPin, X, Pencil
} from 'lucide-react';

const statusColors = {
  'New': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'On Hold': 'bg-gray-100 text-gray-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Offer Extended': 'bg-purple-100 text-purple-700',
  'Offer Accepted': 'bg-green-100 text-green-700',
  'Offer Rejected': 'bg-red-100 text-red-700',
  'Joined': 'bg-emerald-100 text-emerald-700',
  'Converted': 'bg-teal-100 text-teal-700',
  'Withdrawn': 'bg-slate-100 text-slate-700'
};

const interviewStatusColors = {
  'Scheduled': 'bg-blue-100 text-blue-700',
  'Completed': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-700',
  'No Show': 'bg-slate-100 text-slate-700'
};

const recommendationColors = {
  'Strong Hire': 'bg-emerald-100 text-emerald-700',
  'Hire': 'bg-green-100 text-green-700',
  'No Hire': 'bg-red-100 text-red-700',
  'Hold': 'bg-amber-100 text-amber-700'
};

const CandidateDetail = ({ candidateId, onBack }) => {
  const {
    getCandidate, updateCandidate, jobs, fetchJobs,
    fetchInterviews, createInterview, updateInterview, deleteInterview,
    fetchOffers, updateCandidateStage, pipelineTemplates, fetchPipelineTemplates,
    uploadResume, fetchResumeBlobUrl, deleteResume, fetchTags
  } = useRecruitment();

  const [candidate, setCandidate] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Stage change state
  const [stageLoading, setStageLoading] = useState(false);
  const [availableStages, setAvailableStages] = useState([]);

  // Resume state
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Edit candidate dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [editTagInput, setEditTagInput] = useState('');
  const [editSkillInput, setEditSkillInput] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Schedule interview dialog
  const [showInterviewDialog, setShowInterviewDialog] = useState(false);
  const [editingInterviewId, setEditingInterviewId] = useState(null);
  const [interviewForm, setInterviewForm] = useState({
    round_name: '',
    round_number: 1,
    interviewer_name: '',
    scheduled_at: '',
    status: 'Scheduled'
  });

  // Feedback dialog
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    feedback: '',
    recommendation: '',
    status: 'Completed'
  });

  // General feedback (without interview)
  const [showGeneralFeedbackDialog, setShowGeneralFeedbackDialog] = useState(false);
  const [generalFeedbackForm, setGeneralFeedbackForm] = useState({
    round_name: 'General Feedback',
    feedback: '',
    recommendation: '',
    interviewer_name: ''
  });

  useEffect(() => {
    loadCandidateData();
  }, [candidateId]);

  const loadCandidateData = async () => {
    setLoading(true);
    try {
      const [candidateData, interviewsData, offersData] = await Promise.all([
        getCandidate(candidateId),
        fetchInterviews({ candidate_id: candidateId }),
        fetchOffers(candidateId)
      ]);
      
      setCandidate(candidateData);
      setInterviews(interviewsData || []);
      setOffers(offersData || []);
      
      // Initialize edit form
      if (candidateData) {
        setEditForm({
          name: candidateData.name || '',
          email: candidateData.email || '',
          phone: candidateData.phone || '',
          location: candidateData.location || '',
          current_company: candidateData.current_company || '',
          years_of_experience: candidateData.years_of_experience?.toString() || '',
          current_ctc: candidateData.current_ctc?.toString() || '',
          expected_ctc: candidateData.expected_ctc?.toString() || '',
          notice_period: candidateData.notice_period || '',
          notes: candidateData.notes || '',
          job_id: candidateData.job_id || '',
          skills: candidateData.skills || [],
          tags: candidateData.tags || []
        });
      }
      
      await fetchJobs();
      await fetchPipelineTemplates();
      const tagsData = await fetchTags();
      setAllTags(tagsData || []);
    } catch (error) {
      console.error('Error loading candidate:', error);
      toast.error('Failed to load candidate details');
    } finally {
      setLoading(false);
    }
  };

  // Compute available stages when candidate/jobs/templates load
  useEffect(() => {
    if (candidate && jobs.length > 0 && pipelineTemplates.length > 0) {
      let stages = [];
      if (candidate.job_id) {
        const job = jobs.find(j => j.id === candidate.job_id);
        if (job?.pipeline_template_id) {
          const template = pipelineTemplates.find(t => t.id === job.pipeline_template_id);
          stages = template?.stages || [];
        }
      }
      if (stages.length === 0) {
        const defaultTemplate = pipelineTemplates.find(t => t.is_default);
        stages = defaultTemplate?.stages || ['Screening', 'Interview', 'Offer', 'Hired'];
      }
      setAvailableStages(stages);
    }
  }, [candidate, jobs, pipelineTemplates]);

  // Load resume as a blob and hand back an object URL. This is the fix
  // for the "Invalid or expired token" the iframe used to show ~15 min
  // into a session: the old URL had the JWT baked in as ?token=…, and
  // once it expired the iframe fetch failed silently (iframes bypass the
  // axios silent-refresh interceptor). fetchResumeBlobUrl goes through
  // axios, so the interceptor supplies a live Bearer token, and the
  // iframe just points at a blob: URL that never expires until we
  // revoke it.
  useEffect(() => {
    let cancelled = false;
    let issuedUrl = null;
    if (candidate?.resume_file_id) {
      (async () => {
        const r = await fetchResumeBlobUrl(candidateId);
        if (cancelled) {
          // Component unmounted mid-fetch — release the blob so we
          // don't leak the PDF in browser memory.
          if (r.success && r.url) URL.revokeObjectURL(r.url);
          return;
        }
        if (r.success) {
          issuedUrl = r.url;
          setResumeUrl(r.url);
        } else {
          setResumeUrl(null);
          toast.error(r.error || 'Failed to load resume');
        }
      })();
    } else {
      setResumeUrl(null);
    }
    return () => {
      cancelled = true;
      if (issuedUrl) URL.revokeObjectURL(issuedUrl);
    };
  }, [candidate?.resume_file_id, candidateId, fetchResumeBlobUrl]);

  const handleUploadResume = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are supported');
      return;
    }

    setResumeLoading(true);
    const result = await uploadResume(candidateId, file);
    setResumeLoading(false);

    if (result.success) {
      toast.success('Resume uploaded successfully');
      loadCandidateData();
    } else {
      toast.error(result.error || 'Failed to upload resume');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteResume = async () => {
    if (!window.confirm('Delete this resume?')) return;

    setResumeLoading(true);
    const result = await deleteResume(candidateId);
    setResumeLoading(false);

    if (result.success) {
      toast.success('Resume deleted');
      setResumeUrl(null);
      loadCandidateData();
    } else {
      toast.error(result.error || 'Failed to delete resume');
    }
  };

  const handleDownloadResume = () => {
    if (resumeUrl) {
      const link = document.createElement('a');
      link.href = resumeUrl;
      link.download = candidate?.resume_filename || `resume_${candidateId}.pdf`;
      link.click();
    }
  };

  const handleUpdateCandidate = async () => {
    setFormLoading(true);
    const result = await updateCandidate(candidateId, {
      ...editForm,
      years_of_experience: editForm.years_of_experience ? parseFloat(editForm.years_of_experience) : null,
      current_ctc: editForm.current_ctc ? parseFloat(editForm.current_ctc) : null,
      expected_ctc: editForm.expected_ctc ? parseFloat(editForm.expected_ctc) : null,
      job_id: editForm.job_id || null,
      location: editForm.location || null,
      skills: editForm.skills || [],
      tags: editForm.tags || []
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Candidate updated');
      setShowEditDialog(false);
      loadCandidateData();
    } else {
      toast.error(result.error);
    }
  };

  // Tag helpers for edit form
  const addEditTag = (tagValue) => {
    const tag = (tagValue || editTagInput).trim();
    if (tag && !(editForm.tags || []).includes(tag)) {
      setEditForm(prev => ({ ...prev, tags: [...(prev.tags || []), tag] }));
      if (!allTags.includes(tag)) {
        setAllTags(prev => [...prev, tag].sort());
      }
    }
    setEditTagInput('');
    setShowTagSuggestions(false);
  };

  const removeEditTag = (tag) => {
    setEditForm(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }));
  };

  const addEditSkill = () => {
    if (editSkillInput.trim() && !(editForm.skills || []).includes(editSkillInput.trim())) {
      setEditForm(prev => ({ ...prev, skills: [...(prev.skills || []), editSkillInput.trim()] }));
      setEditSkillInput('');
    }
  };

  const removeEditSkill = (skill) => {
    setEditForm(prev => ({ ...prev, skills: (prev.skills || []).filter(s => s !== skill) }));
  };

  const filteredEditTagSuggestions = editTagInput.trim().length > 0
    ? allTags.filter(t => t.toLowerCase().includes(editTagInput.toLowerCase()) && !(editForm.tags || []).includes(t))
    : [];

  const handleStageChange = async (newStage) => {
    if (!newStage || newStage === candidate.current_stage) return;
    setStageLoading(true);
    const result = await updateCandidateStage(candidateId, newStage);
    setStageLoading(false);
    if (result.success) {
      toast.success(`Stage changed to ${newStage}`);
      loadCandidateData();
    } else {
      toast.error(result.error || 'Failed to change stage');
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.round_name || !interviewForm.scheduled_at) {
      toast.error('Round name and scheduled time are required');
      return;
    }

    setFormLoading(true);
    let result;
    if (editingInterviewId) {
      // Edit existing interview
      result = await updateInterview(editingInterviewId, {
        candidate_id: candidateId,
        job_id: candidate?.job_id,
        ...interviewForm
      });
    } else {
      result = await createInterview({
        candidate_id: candidateId,
        job_id: candidate?.job_id,
        ...interviewForm
      });
    }
    setFormLoading(false);

    if (result.success) {
      toast.success(editingInterviewId ? 'Interview updated' : 'Interview scheduled');
      setShowInterviewDialog(false);
      setEditingInterviewId(null);
      setInterviewForm({ round_name: '', round_number: 1, interviewer_name: '', scheduled_at: '', status: 'Scheduled' });
      loadCandidateData();
    } else {
      toast.error(result.error);
    }
  };

  const openEditInterviewDialog = (interview) => {
    setEditingInterviewId(interview.id);
    // Convert ISO scheduled_at to datetime-local input format (yyyy-MM-ddTHH:mm)
    let dt = interview.scheduled_at || '';
    if (dt) {
      const d = new Date(dt);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, '0');
        dt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    setInterviewForm({
      round_name: interview.round_name || '',
      round_number: interview.round_number || 1,
      interviewer_name: interview.interviewer_name || '',
      scheduled_at: dt,
      status: interview.status || 'Scheduled'
    });
    setShowInterviewDialog(true);
  };

  const handleCloseInterviewDialog = (open) => {
    setShowInterviewDialog(open);
    if (!open) {
      setEditingInterviewId(null);
      setInterviewForm({ round_name: '', round_number: 1, interviewer_name: '', scheduled_at: '', status: 'Scheduled' });
    }
  };

  const handleSubmitFeedback = async () => {
    if (!selectedInterview) return;

    setFormLoading(true);
    const result = await updateInterview(selectedInterview.id, {
      ...selectedInterview,
      feedback: feedbackForm.feedback,
      recommendation: feedbackForm.recommendation,
      status: feedbackForm.status
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Feedback submitted');
      setShowFeedbackDialog(false);
      loadCandidateData();
    } else {
      toast.error(result.error);
    }
  };

  const openFeedbackDialog = (interview) => {
    setSelectedInterview(interview);
    setFeedbackForm({
      feedback: interview.feedback || '',
      recommendation: interview.recommendation || '',
      status: interview.status || 'Completed'
    });
    setShowFeedbackDialog(true);
  };

  const handleAddGeneralFeedback = async () => {
    if (!generalFeedbackForm.feedback.trim()) {
      toast.error('Please enter feedback');
      return;
    }

    setFormLoading(true);
    // Create an interview record with just feedback (no scheduled_at)
    const result = await createInterview({
      candidate_id: candidateId,
      job_id: candidate.job_id,
      round_name: generalFeedbackForm.round_name || 'General Feedback',
      round_number: interviews.length + 1,
      interviewer_name: generalFeedbackForm.interviewer_name || 'HR',
      scheduled_at: new Date().toISOString(), // Current time
      status: 'Completed',
      feedback: generalFeedbackForm.feedback,
      recommendation: generalFeedbackForm.recommendation
    });
    setFormLoading(false);

    if (result.success) {
      toast.success('Feedback added');
      setShowGeneralFeedbackDialog(false);
      setGeneralFeedbackForm({ round_name: 'General Feedback', feedback: '', recommendation: '', interviewer_name: '' });
      loadCandidateData();
    } else {
      toast.error(result.error);
    }
  };

  const handleDeleteInterview = async (interviewId) => {
    if (!window.confirm('Delete this interview?')) return;
    
    const result = await deleteInterview(interviewId);
    if (result.success) {
      toast.success('Interview deleted');
      loadCandidateData();
    } else {
      toast.error(result.error);
    }
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job?.title || 'No Position';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
        <p className="text-slate-500">Candidate not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{candidate.name}</h1>
              <Badge className={statusColors[candidate.status]}>{candidate.status}</Badge>
            </div>
            <p className="text-slate-500">{candidate.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <CopyLinkButton params={{ tab: 'candidates', candidate_id: candidate.id }} what="candidate" />
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Edit className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button onClick={() => setShowInterviewDialog(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Schedule Interview
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="resume" data-testid="tab-resume">
            Resume {candidate.resume_file_id && <Badge variant="secondary" className="ml-1 h-5 px-1">1</Badge>}
          </TabsTrigger>
          <TabsTrigger value="interviews">Interviews ({interviews.length})</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Candidate Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{candidate.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{candidate.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{candidate.location || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span>{candidate.current_company || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>{candidate.years_of_experience ? `${candidate.years_of_experience} years` : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-slate-400" />
                    <span>Current: {candidate.current_ctc ? `₹${candidate.current_ctc} LPA` : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-slate-400" />
                    <span>Expected: {candidate.expected_ctc ? `₹${candidate.expected_ctc} LPA` : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>Notice: {candidate.notice_period || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span>{getJobTitle(candidate.job_id)}</span>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-slate-500">Skills</Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {candidate.skills?.length > 0 ? candidate.skills.map(s => (
                      <Badge key={s} variant="secondary">{s}</Badge>
                    )) : <span className="text-slate-400">No skills listed</span>}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-500">Tags</Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {candidate.tags?.length > 0 ? candidate.tags.map(t => (
                      <Badge key={t} variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
                        <Tag className="w-3 h-3" />
                        {t}
                      </Badge>
                    )) : <span className="text-slate-400">No tags</span>}
                  </div>
                </div>

                {candidate.notes && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-slate-500">Notes</Label>
                      <p className="mt-1 text-sm">{candidate.notes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Current Stage</CardTitle>
                </CardHeader>
                <CardContent>
                  {availableStages.length > 0 ? (
                    <Select
                      value={candidate.current_stage || ''}
                      onValueChange={handleStageChange}
                      disabled={stageLoading}
                    >
                      <SelectTrigger className="text-lg font-semibold text-purple-600 border-purple-200" data-testid="stage-select">
                        <SelectValue placeholder={candidate.current_stage || 'New'} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStages.map(stage => (
                          <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-xl font-semibold text-purple-600">{candidate.current_stage || 'New'}</p>
                  )}
                  {stageLoading && <Loader2 className="w-4 h-4 animate-spin text-purple-500 mt-1" />}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg">{candidate.source || 'Unknown'}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Interviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-semibold">{interviews.length}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-green-600">
                        {interviews.filter(i => i.status === 'Completed').length}
                      </p>
                      <p className="text-xs text-slate-500">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-blue-600">
                        {interviews.filter(i => i.status === 'Scheduled').length}
                      </p>
                      <p className="text-xs text-slate-500">Scheduled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <CandidateKeyDates candidate={candidate} interviews={interviews} />
            </div>
          </div>
        </TabsContent>

        {/* Resume Tab */}
        <TabsContent value="resume" className="space-y-4" data-testid="resume-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Resume / CV</CardTitle>
                  <CardDescription>
                    {candidate.resume_filename || 'No resume uploaded'}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf"
                    onChange={handleUploadResume}
                    className="hidden"
                    data-testid="resume-upload-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={resumeLoading}
                    data-testid="upload-resume-btn"
                  >
                    {resumeLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {candidate.resume_file_id ? 'Replace' : 'Upload'} Resume
                  </Button>
                  {candidate.resume_file_id && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleDownloadResume}
                        disabled={resumeLoading}
                        data-testid="download-resume-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDeleteResume}
                        disabled={resumeLoading}
                        className="text-red-500 hover:text-red-600"
                        data-testid="delete-resume-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {candidate.resume_file_id && resumeUrl ? (
                <div className="border rounded-lg overflow-hidden bg-slate-50" data-testid="resume-viewer">
                  <iframe
                    src={resumeUrl}
                    className="w-full h-[70vh] min-h-[500px]"
                    title={`Resume - ${candidate.name}`}
                    data-testid="resume-iframe"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <File className="w-16 h-16 text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No Resume Uploaded</h3>
                  <p className="text-slate-500 mb-6 max-w-md">
                    Upload the candidate's resume to view it here. Only PDF files are supported.
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={resumeLoading}
                  >
                    {resumeLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload Resume
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Interview Schedule</h3>
            <Button onClick={() => setShowInterviewDialog(true)} size="sm">
              <Plus className="w-4 h-4 mr-2" /> Schedule New
            </Button>
          </div>

          {interviews.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No interviews scheduled</p>
                <Button 
                  variant="outline" 
                  onClick={() => setShowInterviewDialog(true)}
                  className="mt-4"
                >
                  Schedule First Interview
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {interviews.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)).map((interview) => (
                <Card key={interview.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{interview.round_name}</span>
                          <Badge variant="outline">Round {interview.round_number}</Badge>
                          <Badge className={interviewStatusColors[interview.status]}>{interview.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {interview.scheduled_at ? new Date(interview.scheduled_at).toLocaleString() : 'Not scheduled'}
                          </span>
                          {interview.interviewer_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {interview.interviewer_name}
                            </span>
                          )}
                        </div>
                        {interview.recommendation && (
                          <Badge className={recommendationColors[interview.recommendation]}>
                            {interview.recommendation}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditInterviewDialog(interview)} title="Edit Interview" data-testid={`interview-edit-btn-${interview.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openFeedbackDialog(interview)} title="Add/Edit Feedback">
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500"
                          onClick={() => handleDeleteInterview(interview.id)}
                          title="Delete Interview"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Interview Feedback</h3>
            <div className="flex gap-2">
              {interviews.filter(i => !i.feedback && i.status !== 'Cancelled').length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Find first interview without feedback
                    const interviewToFeedback = interviews.find(i => !i.feedback && i.status !== 'Cancelled');
                    if (interviewToFeedback) {
                      setSelectedInterview(interviewToFeedback);
                      setFeedbackForm({ feedback: '', recommendation: '', status: 'Completed' });
                      setShowFeedbackDialog(true);
                    }
                  }}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Interview Feedback
                </Button>
              )}
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowGeneralFeedbackDialog(true)}
                className="gap-2 bg-purple-600 hover:bg-purple-700"
              >
                <MessageSquare className="w-4 h-4" />
                Add General Feedback
              </Button>
            </div>
          </div>
          
          {/* Interviews pending feedback */}
          {interviews.filter(i => !i.feedback && i.status !== 'Cancelled').length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-amber-800">Pending Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {interviews.filter(i => !i.feedback && i.status !== 'Cancelled').map(interview => (
                    <div key={interview.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <p className="font-medium text-sm">{interview.round_name} - Round {interview.round_number}</p>
                        <p className="text-xs text-slate-500">
                          {interview.interviewer_name && `${interview.interviewer_name} • `}
                          {interview.scheduled_at && new Date(interview.scheduled_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedInterview(interview);
                          setFeedbackForm({ feedback: '', recommendation: '', status: 'Completed' });
                          setShowFeedbackDialog(true);
                        }}
                      >
                        Add Feedback
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {interviews.filter(i => i.feedback).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No feedback recorded yet</p>
                <p className="text-xs text-slate-400 mt-1">Schedule an interview and add feedback after completion</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {interviews.filter(i => i.feedback).map((interview) => (
                <Card key={interview.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{interview.round_name} - Round {interview.round_number}</CardTitle>
                      {interview.recommendation && (
                        <Badge className={recommendationColors[interview.recommendation]}>
                          {interview.recommendation}
                        </Badge>
                      )}
                    </div>
                    <CardDescription>
                      {interview.interviewer_name && `By ${interview.interviewer_name} • `}
                      {interview.scheduled_at && new Date(interview.scheduled_at).toLocaleDateString()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{interview.feedback}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <h3 className="text-lg font-semibold">Status History</h3>
          
          {!candidate.status_history?.length ? (
            <Card>
              <CardContent className="py-12 text-center">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No activity recorded</p>
              </CardContent>
            </Card>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="relative pl-6 border-l-2 border-slate-200 space-y-6">
                {candidate.status_history.slice().reverse().map((entry, idx) => (
                  <div key={idx} className="relative">
                    <div className="absolute -left-[25px] w-4 h-4 rounded-full bg-purple-100 border-2 border-purple-500" />
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge className={statusColors[entry.status]}>{entry.status}</Badge>
                            <p className="text-sm text-slate-500 mt-1">Stage: {entry.stage}</p>
                            {entry.notes && <p className="text-sm mt-2">{entry.notes}</p>}
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>{new Date(entry.timestamp).toLocaleDateString()}</p>
                            <p>{entry.changed_by}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Candidate Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        {/* Opts out of the primitive's max-h + scroll: the tag suggestions
            below are an absolute (non-portaled) popup that would otherwise
            be clipped by the scroll container. Same layout as before. */}
        <DialogContent className="max-w-lg max-h-[none] overflow-visible">
          <DialogHeader>
            <DialogTitle>Edit Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
            {/* Job Assignment */}
            <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
              <Label className="text-purple-700 font-medium">Assigned Job</Label>
              <Select 
                value={editForm.job_id || 'none'} 
                onValueChange={(v) => setEditForm({ ...editForm, job_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select job..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Job Assigned</SelectItem>
                  {jobs.map(job => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title} {job.department && `(${job.department})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-purple-600">
                Change the job to move this candidate to a different position
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editForm.phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Location / City</Label>
                <Input
                  value={editForm.location || ''}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="e.g. Mumbai, Bangalore"
                  data-testid="edit-candidate-location"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={editForm.current_company || ''}
                  onChange={(e) => setEditForm({ ...editForm, current_company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Experience (Years)</Label>
                <Input
                  type="number"
                  value={editForm.years_of_experience || ''}
                  onChange={(e) => setEditForm({ ...editForm, years_of_experience: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Notice Period</Label>
                <Select 
                  value={editForm.notice_period || ''} 
                  onValueChange={(v) => setEditForm({ ...editForm, notice_period: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Immediate">Immediate</SelectItem>
                    <SelectItem value="15 days">15 days</SelectItem>
                    <SelectItem value="30 days">30 days</SelectItem>
                    <SelectItem value="60 days">60 days</SelectItem>
                    <SelectItem value="90 days">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Current CTC (LPA)</Label>
                <Input
                  type="number"
                  value={editForm.current_ctc || ''}
                  onChange={(e) => setEditForm({ ...editForm, current_ctc: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected CTC (LPA)</Label>
                <Input
                  type="number"
                  value={editForm.expected_ctc || ''}
                  onChange={(e) => setEditForm({ ...editForm, expected_ctc: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* Skills */}
            <div className="space-y-2">
              <Label>Skills</Label>
              <div className="flex gap-2">
                <Input
                  value={editSkillInput}
                  onChange={(e) => setEditSkillInput(e.target.value)}
                  placeholder="Add a skill..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditSkill())}
                />
                <Button type="button" variant="outline" onClick={addEditSkill}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {editForm.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editForm.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      {skill}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeEditSkill(skill)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Tags with Autocomplete */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="relative">
                <div className="flex gap-2">
                  <Input
                    value={editTagInput}
                    onChange={(e) => { setEditTagInput(e.target.value); setShowTagSuggestions(true); }}
                    placeholder="Add a tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditTag())}
                    onFocus={() => setShowTagSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                    data-testid="edit-detail-tag-input"
                  />
                  <Button type="button" variant="outline" onClick={() => addEditTag()}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {showTagSuggestions && filteredEditTagSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
                    {filteredEditTagSuggestions.map((tag, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-1.5 text-sm cursor-pointer hover:bg-purple-50 hover:text-purple-700"
                        onMouseDown={(e) => { e.preventDefault(); addEditTag(tag); }}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {editForm.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editForm.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1 bg-purple-50 text-purple-700 border-purple-200">
                      <Tag className="w-3 h-3" />
                      {tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeEditTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateCandidate} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule / Edit Interview Dialog */}
      <Dialog open={showInterviewDialog} onOpenChange={handleCloseInterviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="interview-dialog-title">{editingInterviewId ? 'Edit Interview' : 'Schedule Interview'}</DialogTitle>
            <DialogDescription>
              {editingInterviewId ? `Reschedule or modify this interview for ${candidate.name}` : `Schedule a new interview for ${candidate.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Interview Round *</Label>
              <Select 
                value={interviewForm.round_name} 
                onValueChange={(v) => setInterviewForm({ ...interviewForm, round_name: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select round type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Phone Screen">Phone Screen</SelectItem>
                  <SelectItem value="Technical Round">Technical Round</SelectItem>
                  <SelectItem value="Coding Round">Coding Round</SelectItem>
                  <SelectItem value="System Design">System Design</SelectItem>
                  <SelectItem value="HR Round">HR Round</SelectItem>
                  <SelectItem value="Manager Round">Manager Round</SelectItem>
                  <SelectItem value="Leadership Round">Leadership Round</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Round Number</Label>
                <Input
                  type="number"
                  min="1"
                  value={interviewForm.round_number}
                  onChange={(e) => setInterviewForm({ ...interviewForm, round_number: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Interviewer</Label>
                <Input
                  value={interviewForm.interviewer_name}
                  onChange={(e) => setInterviewForm({ ...interviewForm, interviewer_name: e.target.value })}
                  placeholder="Name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={interviewForm.scheduled_at}
                onChange={(e) => setInterviewForm({ ...interviewForm, scheduled_at: e.target.value })}
              />
            </div>
            {editingInterviewId && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={interviewForm.status || 'Scheduled'} 
                  onValueChange={(v) => setInterviewForm({ ...interviewForm, status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                    <SelectItem value="No Show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleCloseInterviewDialog(false)}>Cancel</Button>
            <Button onClick={handleScheduleInterview} disabled={formLoading} className="bg-purple-600 hover:bg-purple-700" data-testid="interview-dialog-save-btn">
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingInterviewId ? 'Save Changes' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Interview Feedback</DialogTitle>
            <DialogDescription>
              {selectedInterview?.round_name} - Round {selectedInterview?.round_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={feedbackForm.status} 
                onValueChange={(v) => setFeedbackForm({ ...feedbackForm, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                  <SelectItem value="No Show">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recommendation</Label>
              <Select 
                value={feedbackForm.recommendation || ''} 
                onValueChange={(v) => setFeedbackForm({ ...feedbackForm, recommendation: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recommendation..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Strong Hire">Strong Hire</SelectItem>
                  <SelectItem value="Hire">Hire</SelectItem>
                  <SelectItem value="No Hire">No Hire</SelectItem>
                  <SelectItem value="Hold">Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Feedback</Label>
              <Textarea
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback: e.target.value })}
                rows={5}
                placeholder="Enter detailed feedback..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitFeedback} disabled={formLoading}>
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* General Feedback Dialog (without interview) */}
      <Dialog open={showGeneralFeedbackDialog} onOpenChange={setShowGeneralFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add General Feedback</DialogTitle>
            <DialogDescription>
              Add feedback for {candidate?.name} without scheduling an interview
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Feedback Type</Label>
              <Select 
                value={generalFeedbackForm.round_name} 
                onValueChange={(v) => setGeneralFeedbackForm({ ...generalFeedbackForm, round_name: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Feedback">General Feedback</SelectItem>
                  <SelectItem value="Resume Review">Resume Review</SelectItem>
                  <SelectItem value="Phone Screen">Phone Screen</SelectItem>
                  <SelectItem value="Reference Check">Reference Check</SelectItem>
                  <SelectItem value="Background Check">Background Check</SelectItem>
                  <SelectItem value="HR Notes">HR Notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input
                value={generalFeedbackForm.interviewer_name}
                onChange={(e) => setGeneralFeedbackForm({ ...generalFeedbackForm, interviewer_name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>
            <div className="space-y-2">
              <Label>Recommendation</Label>
              <Select 
                value={generalFeedbackForm.recommendation || 'none'} 
                onValueChange={(v) => setGeneralFeedbackForm({ ...generalFeedbackForm, recommendation: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recommendation..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No recommendation</SelectItem>
                  <SelectItem value="Strong Hire">Strong Hire</SelectItem>
                  <SelectItem value="Hire">Hire</SelectItem>
                  <SelectItem value="No Hire">No Hire</SelectItem>
                  <SelectItem value="Strong No Hire">Strong No Hire</SelectItem>
                  <SelectItem value="Hold">Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Feedback *</Label>
              <Textarea
                value={generalFeedbackForm.feedback}
                onChange={(e) => setGeneralFeedbackForm({ ...generalFeedbackForm, feedback: e.target.value })}
                rows={5}
                placeholder="Enter your feedback or notes about the candidate..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGeneralFeedbackDialog(false)}>Cancel</Button>
            <Button onClick={handleAddGeneralFeedback} disabled={formLoading} className="bg-purple-600 hover:bg-purple-700">
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateDetail;
